import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface MoneyVisualProps {
  amount: number; // In centen
  breakdown?: MoneyBreakdown;
  showCoins?: boolean;
  showBills?: boolean;
  interactive?: boolean;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full panel with tip (shown after answer) */
  mode?: 'stimulus' | 'support';
}
interface MoneyBreakdown {
  bills: {
    value: number; // in euro
    count: number;
  }[];
  coins: {
    value: number; // in centen
    count: number;
  }[];
}
// Euro biljetten en munten
const EURO_BILLS = [500, 200, 100, 50, 20, 10, 5]; // In euro
const EURO_COINS = [200, 100, 50, 20, 10, 5, 2, 1]; // In centen
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
function normalizeCents(amount: number): number {
  if (!isFiniteNumber(amount)) return 0;
  return Math.max(0, Math.round(amount));
}
function normalizeBreakdown(breakdown?: MoneyBreakdown): MoneyBreakdown | null {
  if (!breakdown) return null;
  const bills = Array.isArray(breakdown.bills) ? breakdown.bills : [];
  const coins = Array.isArray(breakdown.coins) ? breakdown.coins : [];
  const safeBills = bills.filter(b => isFiniteNumber(b?.value) && isFiniteNumber(b?.count)).map(b => ({
    value: Math.round(b.value),
    count: Math.max(0, Math.round(b.count))
  })).filter(b => b.count > 0);
  const safeCoins = coins.filter(c => isFiniteNumber(c?.value) && isFiniteNumber(c?.count)).map(c => ({
    value: Math.round(c.value),
    count: Math.max(0, Math.round(c.count))
  })).filter(c => c.count > 0);
  return {
    bills: safeBills,
    coins: safeCoins
  };
}
/**
 * Automatisch geld verdelen in biljetten en munten
 */
function autoBreakdown(cents: number): MoneyBreakdown {
  let remaining = cents;
  const bills: {
    value: number;
    count: number;
  }[] = [];
  const coins: {
    value: number;
    count: number;
  }[] = [];
  // Biljetten (in euro)
  for (const billValue of EURO_BILLS) {
    const billCents = billValue * 100;
    const count = Math.floor(remaining / billCents);
    if (count > 0) {
      bills.push({
        value: billValue,
        count
      });
      remaining -= count * billCents;
    }
  }
  // Munten (in centen)
  for (const coinValue of EURO_COINS) {
    const count = Math.floor(remaining / coinValue);
    if (count > 0) {
      coins.push({
        value: coinValue,
        count
      });
      remaining -= count * coinValue;
    }
  }
  return {
    bills,
    coins
  };
}
function formatMoney(cents: number): string {
  const safe = normalizeCents(cents);
  const euros = Math.floor(safe / 100);
  const remainingCents = safe % 100;
  if (remainingCents === 0) return `€${euros},-`;
  return `€${euros},${remainingCents.toString().padStart(2, '0')}`;
}
function getDenominationDisplay(cents: number): string {
  if (!isFiniteNumber(cents)) return '0';
  if (cents >= 100) return `€${cents / 100}`;
  if (cents >= 10) return `${cents}c`;
  return `${cents}c`;
}
/**
 * IMPROVED: Better visual styling for coins and bills
 */
function getMoneyStyles(cents: number) {
  // Bills
  if (cents === 50000) {
    return {
      container: 'w-40 h-24 rounded-lg shadow-lg',
      bg: 'bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600',
      text: 'text-white font-black text-3xl drop-shadow-md',
      border: 'border-4 border-orange-700/30',
      inner: 'bg-white/20 backdrop-blur-sm',
      label: '€500'
    };
  }
  if (cents === 20000) {
    return {
      container: 'w-36 h-20 rounded-lg shadow-lg',
      bg: 'bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600',
      text: 'text-white font-black text-3xl drop-shadow-md',
      border: 'border-4 border-blue-700/30',
      inner: 'bg-white/20 backdrop-blur-sm',
      label: '€200'
    };
  }
  if (cents === 10000) {
    return {
      container: 'w-36 h-20 rounded-lg shadow-lg',
      bg: 'bg-gradient-to-br from-red-400 via-red-500 to-red-600',
      text: 'text-white font-black text-3xl drop-shadow-md',
      border: 'border-4 border-red-700/30',
      inner: 'bg-white/20 backdrop-blur-sm',
      label: '€100'
    };
  }
  if (cents === 5000) {
    return {
      container: 'w-32 h-20 rounded-lg shadow-lg',
      bg: 'bg-gradient-to-br from-orange-300 via-orange-400 to-orange-500',
      text: 'text-white font-black text-2xl drop-shadow-md',
      border: 'border-4 border-orange-600/30',
      inner: 'bg-white/20 backdrop-blur-sm',
      label: '€50'
    };
  }
  if (cents === 2000) {
    return {
      container: 'w-28 h-16 rounded-lg shadow-lg',
      bg: 'bg-gradient-to-br from-blue-300 via-blue-400 to-blue-500',
      text: 'text-white font-black text-2xl drop-shadow-md',
      border: 'border-4 border-blue-600/30',
      inner: 'bg-white/20 backdrop-blur-sm',
      label: '€20'
    };
  }
  if (cents === 1000) {
    return {
      container: 'w-28 h-16 rounded-lg shadow-lg',
      bg: 'bg-gradient-to-br from-green-300 via-green-400 to-green-500',
      text: 'text-white font-black text-2xl drop-shadow-md',
      border: 'border-4 border-green-600/30',
      inner: 'bg-white/20 backdrop-blur-sm',
      label: '€10'
    };
  }
  if (cents === 500) {
    return {
      container: 'w-24 h-14 rounded-lg shadow-md',
      bg: 'bg-gradient-to-br from-slate-300 via-slate-400 to-slate-500',
      text: 'text-white font-black text-xl drop-shadow-md',
      border: 'border-2 border-slate-600/30',
      inner: 'bg-white/15 backdrop-blur-sm',
      label: '€5'
    };
  }
  // Coins
  if (cents === 200) {
    return {
      container: 'w-20 h-20 rounded-full shadow-xl',
      bg: 'bg-gradient-to-br from-amber-200 via-yellow-100 to-amber-300',
      text: 'text-amber-900 font-black text-lg',
      border: 'border-4 border-amber-400',
      inner: 'bg-gradient-to-br from-slate-200 via-slate-50 to-slate-300 rounded-full w-14 h-14 flex items-center justify-center shadow-inner border-2 border-slate-400/40',
      label: '€2'
    };
  }
  if (cents === 100) {
    return {
      container: 'w-20 h-20 rounded-full shadow-xl',
      bg: 'bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-400',
      text: 'text-amber-900 font-black text-lg',
      border: 'border-4 border-amber-500',
      inner: 'bg-gradient-to-br from-slate-200 via-slate-50 to-slate-300 rounded-full w-12 h-12 flex items-center justify-center shadow-inner border-2 border-slate-400/40',
      label: '€1'
    };
  }
  if (cents === 50) {
    return {
      container: 'w-16 h-16 rounded-full shadow-lg',
      bg: 'bg-gradient-to-br from-amber-300 via-yellow-100 to-amber-400',
      text: 'text-amber-900 font-black text-base',
      border: 'border-2 border-amber-500',
      inner: 'w-full h-full flex items-center justify-center',
      label: '50c'
    };
  }
  if (cents === 20) {
    return {
      container: 'w-14 h-14 rounded-full shadow-lg',
      bg: 'bg-gradient-to-br from-amber-300 via-yellow-100 to-amber-400',
      text: 'text-amber-900 font-black text-sm',
      border: 'border-2 border-amber-500',
      inner: 'w-full h-full flex items-center justify-center',
      label: '20c'
    };
  }
  if (cents === 10) {
    return {
      container: 'w-12 h-12 rounded-full shadow-md',
      bg: 'bg-gradient-to-br from-amber-300 via-yellow-100 to-amber-400',
      text: 'text-amber-900 font-black text-sm',
      border: 'border-2 border-amber-500',
      inner: 'w-full h-full flex items-center justify-center',
      label: '10c'
    };
  }
  if (cents === 5) {
    return {
      container: 'w-12 h-12 rounded-full shadow-md',
      bg: 'bg-gradient-to-br from-orange-300 via-orange-200 to-orange-400',
      text: 'text-orange-900 font-black text-xs',
      border: 'border-2 border-orange-500',
      inner: 'w-full h-full flex items-center justify-center',
      label: '5c'
    };
  }
  if (cents === 2) {
    return {
      container: 'w-10 h-10 rounded-full shadow-sm',
      bg: 'bg-gradient-to-br from-orange-300 via-orange-200 to-orange-400',
      text: 'text-orange-900 font-black text-xs',
      border: 'border-2 border-orange-500',
      inner: 'w-full h-full flex items-center justify-center',
      label: '2c'
    };
  }
  if (cents === 1) {
    return {
      container: 'w-10 h-10 rounded-full shadow-sm',
      bg: 'bg-gradient-to-br from-orange-300 via-orange-200 to-orange-400',
      text: 'text-orange-900 font-black text-xs',
      border: 'border-2 border-orange-500',
      inner: 'w-full h-full flex items-center justify-center',
      label: '1c'
    };
  }
  // Fallback
  return {
    container: 'w-10 h-10 rounded-full shadow-sm',
    bg: 'bg-slate-200',
    text: 'text-slate-800 font-bold',
    border: 'border border-slate-300',
    inner: 'w-full h-full flex items-center justify-center',
    label: getDenominationDisplay(cents)
  };
}
function renderToken(cents: number, interactive: boolean, index: number) {
  const styles = getMoneyStyles(cents);
  return <motion.div key={`${cents}-${index}`} initial={{
    opacity: 0,
    scale: 0.8
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    delay: index * 0.05
  }} whileHover={interactive ? {
    scale: 1.1,
    rotate: cents >= 100 ? 0 : 5
  } : {}} className={`${styles.container} ${styles.bg} ${styles.border} flex items-center justify-center relative overflow-hidden`} data-id="element-7944">
      <div className={`absolute inset-1 rounded ${styles.inner || ''}`} data-id="element-7945" />
      <div className="relative z-10 flex items-center justify-center" data-id="element-7946">
        <span className={styles.text} data-id="element-7947">{styles.label}</span>
      </div>
    </motion.div>;
}
export function MoneyVisual({
  amount,
  breakdown,
  showCoins = true,
  showBills = true,
  interactive = false,
  label,
  mode = 'support'
}: MoneyVisualProps) {
  const safeAmount = useMemo(() => normalizeCents(amount), [amount]);
  const actualBreakdown = useMemo(() => {
    const normalized = normalizeBreakdown(breakdown);
    return normalized ?? autoBreakdown(safeAmount);
  }, [breakdown, safeAmount]);
  // ── STIMULUS MODE: compact — amount + coins/bills only ──────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-7948">
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200" data-id="element-7949">
          <div className="flex items-center justify-between mb-3" data-id="element-7950">
            <span className="text-sm font-bold text-emerald-900" data-id="element-7951">
              {label || 'Bedrag:'}
            </span>
            <span className="text-2xl font-black text-emerald-700" data-id="element-7952">
              {formatMoney(safeAmount)}
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5 justify-center" data-id="element-7953">
            {showBills && actualBreakdown.bills.map((bill, idx) => {
            const cents = bill.value * 100;
            const showCount = Math.min(bill.count, 3);
            return Array.from({
              length: showCount
            }).map((_, i) => <motion.div key={`bill-${bill.value}-${i}`} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: (idx + i) * 0.04
            }} className="scale-75" data-id="element-7954">
                    {renderToken(cents, false, i)}
                  </motion.div>);
          })}
            {showCoins && actualBreakdown.coins.map((coin, idx) => {
            const showCount = Math.min(coin.count, 5);
            return Array.from({
              length: showCount
            }).map((_, i) => <motion.div key={`coin-${coin.value}-${i}`} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: (idx + i) * 0.04
            }} className="scale-75" data-id="element-7955">
                    {renderToken(coin.value, false, i)}
                  </motion.div>);
          })}
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full panel with breakdown and tip (original) ──────────
  return <div className="w-full" data-id="element-7956">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-5 mb-6 border-2 border-emerald-200 shadow-sm" data-id="element-7957">
        <div className="flex items-center justify-between" data-id="element-7958">
          <span className="text-base font-bold text-emerald-900" data-id="element-7959">
            {label || 'Totaal bedrag:'}
          </span>
          <span className="text-3xl font-black text-emerald-700 drop-shadow-sm" data-id="element-7960">
            {formatMoney(safeAmount)}
          </span>
        </div>
      </div>

      <div className="space-y-8" data-id="element-7961">
        {showBills && actualBreakdown.bills.length > 0 && <div data-id="element-7962">
            <h4 className="text-base font-black text-slate-700 mb-4 flex items-center gap-2" data-id="element-7963">
              <span className="text-2xl" data-id="element-7964">💵</span>
              <span data-id="element-7965">Biljetten</span>
            </h4>

            <div className="space-y-4" data-id="element-7966">
              {actualBreakdown.bills.map((bill, idx) => {
            const cents = bill.value * 100;
            const showCount = Math.min(bill.count, 5);
            return <motion.div key={`bill-${bill.value}`} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: idx * 0.08
            }} className="flex items-center gap-4" data-id="element-7967">
                    <div className="flex gap-2 flex-wrap" data-id="element-7968">
                      {Array.from({
                  length: showCount
                }).map((_, i) => renderToken(cents, interactive, i))}
                      {bill.count > showCount && <div className="flex items-center justify-center px-3 py-2 text-slate-600 font-bold text-base bg-slate-100 rounded-lg border border-slate-200" data-id="element-7969">
                          +{bill.count - showCount}
                        </div>}
                    </div>

                    <div className="flex-1 text-right" data-id="element-7970">
                      <span className="text-base text-slate-600 font-medium" data-id="element-7971">
                        {bill.count} × €{bill.value} ={' '}
                        <strong className="text-slate-900 font-bold text-lg" data-id="element-7972">
                          €{bill.count * bill.value}
                        </strong>
                      </span>
                    </div>
                  </motion.div>;
          })}
            </div>
          </div>}

        {showCoins && actualBreakdown.coins.length > 0 && <div data-id="element-7973">
            <h4 className="text-base font-black text-slate-700 mb-4 flex items-center gap-2" data-id="element-7974">
              <span className="text-2xl" data-id="element-7975">🪙</span>
              <span data-id="element-7976">Munten</span>
            </h4>

            <div className="space-y-4" data-id="element-7977">
              {actualBreakdown.coins.map((coin, idx) => {
            const cents = coin.value;
            const showCount = Math.min(coin.count, 10);
            return <motion.div key={`coin-${coin.value}`} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: (actualBreakdown.bills.length + idx) * 0.08
            }} className="flex items-center gap-4" data-id="element-7978">
                    <div className="flex gap-2 flex-wrap" data-id="element-7979">
                      {Array.from({
                  length: showCount
                }).map((_, i) => renderToken(cents, interactive, i))}
                      {coin.count > showCount && <div className="flex items-center justify-center px-3 py-2 text-slate-600 font-bold text-sm bg-slate-100 rounded-lg border border-slate-200" data-id="element-7980">
                          +{coin.count - showCount}
                        </div>}
                    </div>

                    <div className="flex-1 text-right" data-id="element-7981">
                      <span className="text-base text-slate-600 font-medium" data-id="element-7982">
                        {coin.count} × {getDenominationDisplay(cents)} ={' '}
                        <strong className="text-slate-900 font-bold text-lg" data-id="element-7983">
                          {formatMoney(coin.count * cents)}
                        </strong>
                      </span>
                    </div>
                  </motion.div>;
          })}
            </div>
          </div>}
      </div>

      <div className="mt-6 bg-blue-50 rounded-xl p-4 border-2 border-blue-200" data-id="element-7984">
        <p className="text-sm text-blue-900 leading-relaxed" data-id="element-7985">
          <span className="font-bold" data-id="element-7986">💡 Tip:</span> Kies eerst grote biljetten,
          daarna kleinere, en werk af met munten. Zo heb je meestal minder stuks
          nodig.
        </p>
      </div>
    </div>;
}